import { resolveServerBaseURL } from "@/lib/server-url";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	ArrowRight,
	CheckCircle2,
	Copy,
	Download,
	FileKey2,
	FileOutput,
	Loader2,
	Sparkles,
	WandSparkles,
} from "lucide-react";
import * as React from "react";
import { authClient } from "@/lib/auth-client";

type DemoContent = {
	eyebrow: string;
	title: string;
	description: string;
	primaryCta: string;
	secondaryCta: string;
	checklistTitle: string;
	checklistDescription: string;
	checklistItems: [string, string, string];
};

const DEFAULT_CONTENT: DemoContent = {
	eyebrow: "First PDF walkthrough",
	title: "Edit the content and generate a branded onboarding PDF.",
	description:
		"This document uses the same React component for the live preview and the generated PDF, which keeps the demo much simpler and more reliable.",
	primaryCta: "Ship first PDF",
	secondaryCta: "Adjust the layout",
	checklistTitle: "Onboarding checklist",
	checklistDescription:
		"Tweak this copy, colors, or spacing and render the updated PDF.",
	checklistItems: [
		"Create API key",
		"Edit the onboarding copy",
		"Generate the watermarked PDF",
	],
};

const INITIAL_MESSAGE =
	"The generated PDF will appear here once Chromium finishes the demo render.";

export function OnboardingPdfDemo({
	sessionEmail,
}: {
	sessionEmail: string;
}) {
	const [apiKey, setApiKey] = React.useState<string | null>(null);
	const [apiKeyError, setApiKeyError] = React.useState<string | null>(null);
	const [isCreatingKey, setIsCreatingKey] = React.useState(false);
	const [runState, setRunState] = React.useState<
		"idle" | "rendering" | "complete" | "error"
	>("idle");
	const [runMessage, setRunMessage] = React.useState(INITIAL_MESSAGE);
	const [runError, setRunError] = React.useState<string | null>(null);
	const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
	const [previewHtml, setPreviewHtml] = React.useState<string>("");
	const [previewError, setPreviewError] = React.useState<string | null>(null);
	const [content, setContent] = React.useState<DemoContent>(DEFAULT_CONTENT);

	React.useEffect(() => {
		let cancelled = false;

		void (async () => {
			try {
				const html = await renderDocumentHtml(content);
				if (!cancelled) {
					setPreviewHtml(html);
					setPreviewError(null);
				}
			} catch (error) {
				if (!cancelled) {
					setPreviewError(
						error instanceof Error
							? error.message
							: "Failed to render the current document preview.",
					);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [content]);

	React.useEffect(() => {
		return () => {
			if (pdfUrl) {
				URL.revokeObjectURL(pdfUrl);
			}
		};
	}, [pdfUrl]);

	async function createApiKey() {
		setIsCreatingKey(true);
		setApiKeyError(null);

		try {
			const sessionBeforeKey = await authClient.getSession();
			const sessionData = sessionBeforeKey.data?.session as
				| { activeOrganizationId?: string }
				| undefined;

			if (!sessionData?.activeOrganizationId) {
				const listRes = await authClient.organization.list();
				let organizationId = listRes.data?.[0]?.id;

				if (!organizationId) {
					const fallbackName = sessionBeforeKey.data?.user?.name
						? `${sessionBeforeKey.data.user.name}'s Organization`
						: "Personal Organization";
					const slug = `onboarding-${Date.now().toString(36)}`;
					const createRes = await authClient.organization.create({
						name: fallbackName,
						slug,
					});

					if (createRes.error || !createRes.data?.id) {
						throw new Error("Failed to create an organization for this account.");
					}

					organizationId = createRes.data.id;
				}

				const setActiveRes = await authClient.organization.setActive({
					organizationId,
				});

				if (setActiveRes.error) {
					throw new Error(
						setActiveRes.error.message ?? "Failed to activate the current organization.",
					);
				}
			}

			const apiKeyResult = await authClient.apiKey.create({
				name: "Onboarding Demo Key",
			});

			if (apiKeyResult.error || !apiKeyResult.data?.key) {
				throw new Error(apiKeyResult.error?.message ?? "Failed to create API key.");
			}

			setApiKey(apiKeyResult.data.key);
		} catch (error) {
			setApiKeyError(
				error instanceof Error ? error.message : "Failed to create API key.",
			);
		} finally {
			setIsCreatingKey(false);
		}
	}

	async function handleGeneratePdf() {
		setRunState("rendering");
		setRunError(null);
		setRunMessage("Running Chromium with your edited components...");

		try {
			const html = await renderDocumentHtml(content);
			const response = await fetch(`${resolveServerBaseURL()}/pdf/watermarked`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...(apiKey ? { "x-api-key": apiKey } : {}),
				},
				body: JSON.stringify({
					html,
					pdfOptions: {
						format: "A4",
						margin: {
							top: 18,
							right: 18,
							bottom: 18,
							left: 18,
						},
					},
				}),
			});

			if (!response.ok) {
				const text = await response.text().catch(() => "");
				throw new Error(text || "Failed to generate the watermarked PDF.");
			}

			const blob = await response.blob();
			setPdfUrl((existingUrl) => {
				if (existingUrl) {
					URL.revokeObjectURL(existingUrl);
				}
				return URL.createObjectURL(blob);
			});
			setRunState("complete");
			setRunMessage("Chromium finished rendering your watermarked demo PDF.");
		} catch (error) {
			setRunState("error");
			setRunError(
				error instanceof Error
					? error.message
					: "Failed to generate the watermarked PDF.",
			);
			setRunMessage("The render failed before a PDF could be returned.");
		}
	}

	function updateField<Key extends keyof DemoContent>(
		key: Key,
		value: DemoContent[Key],
	) {
		setContent((existing) => ({ ...existing, [key]: value }));
	}

	function updateChecklistItem(index: 0 | 1 | 2, value: string) {
		setContent((existing) => {
			const checklistItems = [...existing.checklistItems] as DemoContent["checklistItems"];
			checklistItems[index] = value;
			return { ...existing, checklistItems };
		});
	}

	return (
		<div className="min-h-[calc(100svh-58px)] bg-[#efe6d3] px-4 py-8 text-slate-900 sm:px-6 lg:px-10">
			<div className="mx-auto max-w-7xl space-y-8">
				<section className="relative overflow-hidden rounded-[32px] border border-slate-900/10 bg-[#fcfaf5] p-8 shadow-[0_30px_100px_rgba(90,68,29,0.12)] sm:p-10">
					<div className="absolute inset-y-0 right-0 hidden w-1/3 bg-[radial-gradient(circle_at_top,_rgba(228,179,88,0.28),_transparent_58%)] lg:block" />
					<div className="relative flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
						<div className="max-w-3xl space-y-5">
							<div className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-100/70 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-amber-800">
								<WandSparkles className="h-4 w-4" />
								Onboarding Demo
							</div>
							<h1 className="max-w-3xl text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
								Create your first PDF from one dedicated React document.
							</h1>
							<p className="max-w-2xl text-lg leading-8 text-slate-600">
								The editable form, the live preview, and the generated PDF now all share the same component tree.
							</p>
						</div>
						<div className="grid gap-3 rounded-[28px] border border-slate-900/10 bg-white/90 p-5 text-sm shadow-sm backdrop-blur sm:min-w-[300px]">
							<div className="flex items-center justify-between gap-4">
								<span className="text-slate-500">Signed in as</span>
								<span className="truncate font-medium">{sessionEmail}</span>
							</div>
							<div className="flex items-center justify-between gap-4">
								<span className="text-slate-500">Demo flow</span>
								<span className="font-medium text-emerald-700">Simplified render path</span>
							</div>
						</div>
					</div>
				</section>

				<div className="grid gap-6 xl:grid-cols-[1.35fr_0.9fr]">
					<div className="space-y-6">
						<StepCard
							icon={FileKey2}
							title="Create a useprint API key"
							description="Generate a demo key for this signed-in account and reuse it for the watermarked PDF request."
							status={apiKey ? "Completed" : isCreatingKey ? "Creating" : "Not created"}
							tint={apiKey ? "success" : "default"}
						>
							<div className="flex flex-col gap-4">
								<Button
									onClick={createApiKey}
									disabled={isCreatingKey}
									className="w-full sm:w-auto"
								>
									{isCreatingKey ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											Creating key
										</>
									) : apiKey ? (
										<>
											<CheckCircle2 className="mr-2 h-4 w-4" />
											API key ready
										</>
									) : (
										"Create API key"
									)}
								</Button>

								<div className="rounded-2xl border border-slate-900/10 bg-slate-950 p-4 text-sm text-white shadow-inner">
									<p className="mb-2 font-medium text-slate-200">Current key</p>
									<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
										<code className="overflow-x-auto text-xs text-emerald-300">
											{apiKey ?? "Not created yet"}
										</code>
										<Button
											variant="outline"
											onClick={() => apiKey && navigator.clipboard.writeText(apiKey)}
											disabled={!apiKey}
											className="border-white/20 bg-white/5 text-white hover:bg-white/10"
										>
											<Copy className="mr-2 h-4 w-4" />
											Copy
										</Button>
									</div>
								</div>

								{apiKeyError ? <p className="text-sm text-red-600">{apiKeyError}</p> : null}
							</div>
						</StepCard>

						<StepCard
							icon={Sparkles}
							title="Edit the demo content"
							description="Update the document fields directly, preview the result, then render the same component into a PDF."
							status={apiKey ? "Ready" : "Create key first"}
						>
							<div className="overflow-hidden rounded-[28px] border border-slate-900/10 bg-[#fffdf8] shadow-sm">
								<div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-900/10 px-5 py-4">
									<div>
										<p className="font-medium text-slate-950">Document editor</p>
										<p className="text-sm text-slate-500">
											Change the copy once and use it for both preview and PDF generation.
										</p>
									</div>
									<Button onClick={handleGeneratePdf} disabled={!apiKey || runState === "rendering"}>
										{runState === "rendering" ? (
											<>
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												Rendering PDF
											</>
										) : (
											<>
												<WandSparkles className="mr-2 h-4 w-4" />
												Generate PDF
											</>
										)}
									</Button>
								</div>

								<div className="grid gap-px bg-slate-900/10 lg:grid-cols-[0.95fr_1.05fr]">
									<div className="grid gap-4 bg-[#fffdf8] p-5">
										<LabeledInput
											label="Eyebrow"
											value={content.eyebrow}
											onChange={(value) => updateField("eyebrow", value)}
										/>
										<LabeledTextarea
											label="Title"
											rows={3}
											value={content.title}
											onChange={(value) => updateField("title", value)}
										/>
										<LabeledTextarea
											label="Description"
											rows={4}
											value={content.description}
											onChange={(value) => updateField("description", value)}
										/>
										<div className="grid gap-4 md:grid-cols-2">
											<LabeledInput
												label="Primary CTA"
												value={content.primaryCta}
												onChange={(value) => updateField("primaryCta", value)}
											/>
											<LabeledInput
												label="Secondary CTA"
												value={content.secondaryCta}
												onChange={(value) => updateField("secondaryCta", value)}
											/>
										</div>
										<div className="grid gap-4">
											<LabeledInput
												label="Checklist title"
												value={content.checklistTitle}
												onChange={(value) => updateField("checklistTitle", value)}
											/>
											<LabeledTextarea
												label="Checklist description"
												rows={3}
												value={content.checklistDescription}
												onChange={(value) => updateField("checklistDescription", value)}
											/>
											<LabeledInput
												label="Checklist item 1"
												value={content.checklistItems[0]}
												onChange={(value) => updateChecklistItem(0, value)}
											/>
											<LabeledInput
												label="Checklist item 2"
												value={content.checklistItems[1]}
												onChange={(value) => updateChecklistItem(1, value)}
											/>
											<LabeledInput
												label="Checklist item 3"
												value={content.checklistItems[2]}
												onChange={(value) => updateChecklistItem(2, value)}
											/>
										</div>
									</div>

									<div className="min-w-0 bg-white">
										<div className="border-b border-slate-900/10 px-4 py-3 text-sm font-medium text-slate-500">
											Live preview
										</div>
										{previewError ? (
											<div className="flex h-[560px] items-center justify-center px-8 text-center text-sm leading-7 text-red-600">
												{previewError}
											</div>
										) : (
											<iframe
												title="Document preview"
												srcDoc={previewHtml}
												className="h-[560px] w-full bg-white"
											/>
										)}
									</div>
								</div>

								<div className="border-t border-slate-900/10 bg-white px-5 py-4 text-sm text-slate-500">
									<p>
										{apiKey
											? "The generated API key is included in the request headers for this demo run."
											: "Create an API key above before generating the demo PDF."}
									</p>
								</div>
							</div>
						</StepCard>
					</div>

					<div className="space-y-6">
						<StepCard
							icon={FileOutput}
							title="Running..."
							description="This panel follows the current Chromium render and swaps into a PDF preview as soon as the response is ready."
							status={
								runState === "complete"
									? "Finished"
									: runState === "rendering"
										? "Rendering"
										: runState === "error"
											? "Failed"
											: "Waiting"
							}
							tint={
								runState === "complete"
									? "success"
									: runState === "error"
										? "danger"
										: "default"
							}
						>
							<div className="space-y-4">
								<div className="rounded-[24px] border border-slate-900/10 bg-slate-950 px-5 py-4 text-sm text-slate-200">
									<div className="flex items-center gap-3 font-medium text-white">
										{runState === "rendering" ? (
											<Loader2 className="h-4 w-4 animate-spin text-amber-300" />
										) : runState === "complete" ? (
											<CheckCircle2 className="h-4 w-4 text-emerald-400" />
										) : (
											<ArrowRight className="h-4 w-4 text-slate-400" />
										)}
										<span>{runMessage}</span>
									</div>
									{runError ? <p className="mt-3 text-red-300">{runError}</p> : null}
								</div>

								<div className="overflow-hidden rounded-[28px] border border-slate-900/10 bg-white shadow-inner">
									{pdfUrl ? (
										<iframe
											title="Generated PDF preview"
											src={pdfUrl}
											className="h-[520px] w-full"
										/>
									) : (
										<div className="flex h-[520px] items-center justify-center px-8 text-center text-sm leading-7 text-slate-500">
											The final PDF preview appears here after you generate from the editor.
										</div>
									)}
								</div>
							</div>
						</StepCard>

						<StepCard
							icon={Download}
							title="PDF Created!"
							description="Once Chromium finishes, the confirmation step switches from waiting state to a direct PDF download."
							status={pdfUrl ? "Ready to download" : "Waiting"}
							tint={pdfUrl ? "success" : "default"}
						>
							<div className="rounded-[24px] border border-emerald-200 bg-emerald-50 p-5 text-emerald-950">
								<p className="text-lg font-semibold">PDF Created!</p>
								<p className="mt-2 text-sm leading-7 text-emerald-900/80">
									Your edited document has been rendered into a watermarked demo PDF and is ready for download.
								</p>
								<Button
									disabled={!pdfUrl}
									className="mt-4 w-full sm:w-auto"
									onClick={() => {
										if (!pdfUrl) {
											return;
										}
										const link = document.createElement("a");
										link.href = pdfUrl;
										link.download = "useprint-demo.pdf";
										link.click();
									}}
								>
									<Download className="mr-2 h-4 w-4" />
									Download PDF
								</Button>
							</div>
						</StepCard>
					</div>
				</div>
			</div>
		</div>
	);
}

function StepCard({
	children,
	description,
	icon: Icon,
	status,
	tint = "default",
	title,
}: {
	children: React.ReactNode;
	description: string;
	icon: React.ComponentType<{ className?: string }>;
	status: string;
	tint?: "default" | "success" | "danger";
	title: string;
}) {
	return (
		<Card className="rounded-[32px] border-slate-900/10 bg-[#fcfaf5] py-0 shadow-[0_20px_70px_rgba(90,68,29,0.08)]">
			<CardHeader className="border-b border-slate-900/10 px-6 py-6 sm:px-8">
				<div className="flex flex-wrap items-start justify-between gap-4">
					<div className="space-y-3">
						<div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-950 text-white">
							<Icon className="h-5 w-5" />
						</div>
						<div>
							<CardTitle className="text-2xl text-slate-950">{title}</CardTitle>
							<CardDescription className="mt-2 max-w-2xl text-base leading-7 text-slate-600">
								{description}
							</CardDescription>
						</div>
					</div>
					<div
						className={cn(
							"inline-flex items-center rounded-full px-4 py-2 text-sm font-medium",
							tint === "success" && "bg-emerald-100 text-emerald-800",
							tint === "danger" && "bg-red-100 text-red-700",
							tint === "default" && "bg-slate-100 text-slate-600",
						)}
					>
						{status}
					</div>
				</div>
			</CardHeader>
			<CardContent className="px-6 py-6 sm:px-8 sm:py-8">{children}</CardContent>
		</Card>
	);
}

function LabeledInput({
	label,
	onChange,
	value,
}: {
	label: string;
	onChange: (value: string) => void;
	value: string;
}) {
	return (
		<label className="grid gap-2 text-sm font-medium text-slate-700">
			<span>{label}</span>
			<input
				value={value}
				onChange={(event) => onChange(event.target.value)}
				className="min-h-11 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-200"
			/>
		</label>
	);
}

function LabeledTextarea({
	label,
	onChange,
	rows,
	value,
}: {
	label: string;
	onChange: (value: string) => void;
	rows: number;
	value: string;
}) {
	return (
		<label className="grid gap-2 text-sm font-medium text-slate-700">
			<span>{label}</span>
			<textarea
				rows={rows}
				value={value}
				onChange={(event) => onChange(event.target.value)}
				className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-200"
			/>
		</label>
	);
}

function OnboardingDemoDocument({
	content,
}: {
	content: DemoContent;
}) {
	return null;
}

async function renderDocumentHtml(content: DemoContent) {
	OnboardingDemoDocument({ content });
	return Promise.resolve(createDemoHtml(content));
}

function createDemoHtml(content: DemoContent) {
	const safe = {
		eyebrow: escapeHtml(content.eyebrow),
		title: escapeHtml(content.title),
		description: escapeHtml(content.description),
		primaryCta: escapeHtml(content.primaryCta),
		secondaryCta: escapeHtml(content.secondaryCta),
		checklistTitle: escapeHtml(content.checklistTitle),
		checklistDescription: escapeHtml(content.checklistDescription),
		checklistItems: content.checklistItems.map((item) => escapeHtml(item)),
	};

	return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>usePrint Demo PDF</title>
    <style>
      * { box-sizing: border-box; }
      body {
        margin: 0;
        font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        background: #f5efe3;
        color: #0f172a;
      }
      .page {
        min-height: 1122px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 48px;
        background: #f5efe3;
      }
      .sheet {
        width: 100%;
        border-radius: 36px;
        border: 1px solid rgba(15, 23, 42, 0.1);
        background: white;
        padding: 40px;
        box-shadow: 0 30px 80px rgba(115, 92, 52, 0.12);
      }
      .badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 32px;
        border-radius: 999px;
        border: 1px solid #fcd34d;
        background: #fffbeb;
        padding: 8px 16px;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.3em;
        text-transform: uppercase;
        color: #b45309;
      }
      .grid {
        display: grid;
        gap: 32px;
        grid-template-columns: minmax(0, 1.4fr) minmax(300px, 0.9fr);
      }
      .eyebrow {
        margin: 0 0 20px;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 0.25em;
        text-transform: uppercase;
        color: #64748b;
      }
      .title {
        max-width: 640px;
        margin: 0 0 20px;
        font-size: 56px;
        line-height: 1.02;
        font-weight: 700;
        letter-spacing: -0.04em;
        color: #020617;
      }
      .description {
        max-width: 640px;
        margin: 0;
        font-size: 22px;
        line-height: 1.7;
        color: #475569;
      }
      .actions {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        padding-top: 20px;
      }
      .action-primary,
      .action-secondary {
        border-radius: 999px;
        padding: 14px 20px;
        font-size: 14px;
        font-weight: 600;
      }
      .action-primary {
        background: #020617;
        color: white;
      }
      .action-secondary {
        border: 1px solid #cbd5e1;
        background: white;
        color: #0f172a;
      }
      .panel {
        border-radius: 28px;
        border: 1px solid #e2e8f0;
        background: #020617;
        padding: 24px;
        color: white;
      }
      .panel h2 {
        margin: 0;
        font-size: 30px;
        line-height: 1.1;
      }
      .panel p {
        margin: 8px 0 0;
        font-size: 14px;
        line-height: 1.7;
        color: #cbd5e1;
      }
      .items {
        margin-top: 24px;
        display: grid;
        gap: 16px;
      }
      .item,
      .item-success {
        border-radius: 20px;
        padding: 16px;
        font-size: 14px;
        line-height: 1.6;
      }
      .item {
        background: rgba(255, 255, 255, 0.1);
        color: #e2e8f0;
      }
      .item strong {
        color: white;
      }
      .item-success {
        background: rgba(52, 211, 153, 0.2);
        color: #d1fae5;
      }
      @media (max-width: 900px) {
        .page { padding: 24px; }
        .sheet { padding: 28px; }
        .grid { grid-template-columns: 1fr; }
        .title { font-size: 40px; }
        .description { font-size: 18px; }
      }
    </style>
  </head>
  <body>
    <main class="page">
      <section class="sheet">
        <div class="badge">usePrint Demo PDF</div>
        <div class="grid">
          <div>
            <p class="eyebrow">${safe.eyebrow}</p>
            <h1 class="title">${safe.title}</h1>
            <p class="description">${safe.description}</p>
            <div class="actions">
              <div class="action-primary">${safe.primaryCta}</div>
              <div class="action-secondary">${safe.secondaryCta}</div>
            </div>
          </div>
          <aside class="panel">
            <h2>${safe.checklistTitle}</h2>
            <p>${safe.checklistDescription}</p>
            <div class="items">
              <div class="item"><strong>1. ${safe.checklistItems[0]}</strong></div>
              <div class="item"><strong>2. ${safe.checklistItems[1]}</strong></div>
              <div class="item-success"><strong>3. ${safe.checklistItems[2]}</strong></div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  </body>
</html>`;
}

function escapeHtml(value: string) {
	return value
		.replaceAll("&", "&amp;")
		.replaceAll("<", "&lt;")
		.replaceAll(">", "&gt;")
		.replaceAll('"', "&quot;")
		.replaceAll("'", "&#39;");
}
